import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject } from 'rxjs';

import { RbacService } from '../../common-api/system-management-api/api/rbac.service';
import { SystemPermissionViewModel } from '../../common-api/system-management-api/model/systemPermissionViewModel';
import { UserPermissionViewModel } from '../../common-api/system-management-api/model/userPermissionViewModel';
import { UserProfileViewModel } from '../../common-api/system-management-api/model/userProfileViewModel';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { BusinessErrorEnum } from '../enums/business-error.enum';
import { AdvanceError } from '../error';
import { isNullOrEmpty } from '../utils/common-functions';

const GRAPH_ENDPOINT = 'https://graph.microsoft.com/v1.0/me';

/**
 * 使用者資訊服務
 *
 * @export
 * @class UserProfileService
 */
@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private loginMail = '';
  userProfile$ = new Subject<UserProfileViewModel>();
  userPermission$ = new Subject<UserPermissionViewModel>();

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private rbacService: RbacService,
    private logger: NGXLogger
  ) {}

  /**
   * 使用者 Email
   */
  get email(): string {
    return this.userProfile.email ?? this.loginMail;
  }

  /**
   * 使用者員編
   */
  get userNo(): string | undefined {
    const userInfo = this.userProfile;
    return userInfo.userNo;
  }

  /**
   * 使用者權限列表
   */
  get userPermission(): UserPermissionViewModel {
    const userPermission = this.localStorageService.getItem('userPermission');
    return userPermission ?? {};
  }

  /**
   * 使用者資訊
   */
  get userProfile(): UserProfileViewModel {
    const profile = this.localStorageService.getItem('userProfile');
    return profile ?? {};
  }

  /**
   * 儲存使用者資訊(Azure AD)
   *
   * @return {*}  {Observable<void>}
   * @memberof UserProfileService
   */
  setProfileAzureAD(): Observable<void> {
    const observable$ = new Observable<void>((observer) => {
      new Promise<void>(async (resolve, reject) => {
        try {
          const profile = await this.getProfileGraph().toPromise();
          console.log('setProfileAzureAD', profile);
          this.loginMail = profile?.mail;
          const userInfo = await this.rbacService
            .rbacUserProfileEmailAddressEmailAddressGet(this.loginMail)
            .toPromise();
          if (isNullOrEmpty(userInfo)) {
            this.logger.error('User profile is empty.');
            this.localStorageService.removeItem('userProfile');
            reject(
              new AdvanceError(
                "Sorry. You don't have enough permissions",
                'System Notification',
                BusinessErrorEnum.userNotExist
              )
            );
            return;
          }
          this.localStorageService.setItem('userProfile', userInfo);
          if (userInfo) {
            this.userProfile$.next(userInfo);

            const permissions = await this.rbacService
              .rbacPermissionUserUserNoGet(userInfo.userNo!)
              .toPromise();
            if (isNullOrEmpty(permissions)) {
              this.logger.error('Permissions is empty.');
              reject(
                new AdvanceError(
                  "Sorry. You don't have enough permissions",
                  'System Notification'
                )
              );
              return;
            }
            this.localStorageService.setItem('userPermission', permissions);
            if (permissions) this.userPermission$.next(permissions);

            resolve();
          }
        } catch (err: any) {
          this.logger.error(err);
          if (err.hasOwnProperty('errorCode')) {
            const ignoreCodes = [BusinessErrorEnum.userNotExist];
            if (ignoreCodes.includes(err.code)) {
              return;
            }
          }
          reject(new AdvanceError('Fail to login.'));
        }
      })
        .then(() => observer.next())
        .catch((err) => observer.error(err))
        .finally(() => observer.complete());
    });
    return observable$;
  }

  /**
   * 儲存使用者設定(一般)
   *
   * @param {string} mail 登入 Email
   * @return {*}  {Observable<void>}
   * @memberof UserProfileService
   */
  setProfile(mail: string): Observable<void> {
    this.loginMail = mail;
    const observable$ = new Observable<void>((observer) => {
      new Promise<void>(async (resolve, reject) => {
        try {
          const userInfo = await this.rbacService
            .rbacUserProfileEmailAddressEmailAddressGet(this.loginMail)
            .toPromise();
          if (isNullOrEmpty(userInfo)) {
            this.logger.error('User profile is empty.');
            this.localStorageService.removeItem('userProfile');
            reject(
              new AdvanceError(
                "Sorry. You don't have enough permissions",
                'System Notification',
                BusinessErrorEnum.userNotExist
              )
            );
            return;
          }
          if (userInfo) {
            this.localStorageService.setItem('userProfile', userInfo);
            this.userProfile$.next(userInfo);

            const permissions = await this.rbacService
              .rbacPermissionUserUserNoGet(userInfo.userNo!)
              .toPromise();
            if (isNullOrEmpty(permissions)) {
              this.logger.error('Permissions is empty.');
              reject(
                new AdvanceError(
                  "Sorry. You don't have enough permissions",
                  'System Notification'
                )
              );
              return;
            }
            this.localStorageService.setItem('userPermission', permissions);
            if (permissions) this.userPermission$.next(permissions);

            resolve();
          }
        } catch (err: any) {
          this.logger.error(err);
          if (err.hasOwnProperty('errorCode')) {
            const ignoreCodes = [BusinessErrorEnum.userNotExist];
            if (ignoreCodes.includes(err.code)) {
              return;
            }
          }
          reject(new AdvanceError('Fail to login.'));
        }
      })
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((err) => {
          observer.error(err);
          observer.complete();
        });
    });
    return observable$;
  }

  /**
   * 檢查是否有權限
   *
   * @param {string} functionCode 功能代碼
   * @param {string} privilegeCode 權限代碼
   * @return {*}  {boolean}
   * @memberof UserProfileService
   */
  checkHasPermission(functionCode: string, privilegeCode: string): boolean {
    const permissions = (this.userPermission?.rolePermissions ?? []).reduce(
      (array, rolePermission) => {
        return array.concat(rolePermission.systemPermissions ?? []);
      },
      [] as SystemPermissionViewModel[]
    );

    return (
      permissions.filter(
        (x) =>
          x.functionCode === functionCode && x.privilegeCode === privilegeCode
      ).length > 0
    );
  }

  /**
   * 從 Azure AD 取使用者資訊
   *
   * @private
   * @return {*}  {Observable<any>}
   * @memberof UserProfileService
   */
  private getProfileGraph(): Observable<any> {
    return this.http.get(GRAPH_ENDPOINT);
  }
}
